/**
 * KEY-815: whether to nudge "put a calendar on this dial", and remembering a "Not now".
 *
 * A dial with a section of days (My Seven Days) shows nothing but empty days until someone's calendar is on it, and
 * putting one there is two steps in two places — connect it in Your settings, then add it under the dial's Settings →
 * Calendars. So a slim card sits above the dial until a calendar is on it, for whoever can put one there (the server
 * says: editors and admins). Adding one puts it away for good (the condition stops holding); "Not now" puts it away
 * for this dial in this browser, like the add-people prompt beside it.
 *
 * Every storage access is wrapped: a blocked or full store must never take the workspace down.
 */
import type { InstanceCalendarSourceList } from "@keydials/api-client";

export const CALENDAR_SETUP_DISMISSED_KEY_PREFIX = "kde.calendar-setup-dismissed.v1.";

type PromptStorage = Pick<Storage, "getItem" | "setItem">;

const keyFor = (instanceId: string) => `${CALENDAR_SETUP_DISMISSED_KEY_PREFIX}${instanceId}`;

function defaultStorage(): PromptStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** What the card offers: nothing, adding a calendar the viewer already connected, or connecting one first. */
export type CalendarSetupStep = "none" | "add" | "connect";

export function calendarSetupStep(list: Pick<InstanceCalendarSourceList, "sources" | "canAdd" | "myOtherConnections"> | null): CalendarSetupStep {
  if (!list || !list.canAdd || list.sources.length > 0) return "none";
  return list.myOtherConnections.length > 0 ? "add" : "connect";
}

export function isCalendarSetupDismissed(instanceId: string, storage: PromptStorage | null = defaultStorage()): boolean {
  try {
    return storage?.getItem(keyFor(instanceId)) === "1";
  } catch {
    return false;
  }
}

export function dismissCalendarSetup(instanceId: string, storage: PromptStorage | null = defaultStorage()): void {
  try {
    storage?.setItem(keyFor(instanceId), "1");
  } catch {
    // Storage full, blocked, or disabled. The card returns next load; a crash would be worse.
  }
}
